import type { IAuthLoginRes } from '@/api/types/login';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue'; // 修复：导入 computed
import {
  logout as _logout,
  refreshToken as _refreshToken,
  wxLogin as _wxLogin,
  getWxCode,
} from '@/api/login';
import { isDoubleTokenRes, isSingleTokenRes } from '@/api/types/login';
import { isNeedLoginMode, LOGIN_PAGE } from '@/router/config';
import { isPageTabbar } from '@/tabbar/store';
import { getAllPages, HOME_PAGE } from '@/utils';
import { msg } from '@/utils/modal';
import { useUserStore } from './user';

/**
 * 是否是双token模式
 */
export const isDoubleTokenMode = import.meta.env.VITE_AUTH_MODE === 'double';
// 初始化状态
const tokenInfoState = isDoubleTokenMode
  ? {
      accessToken: '',
      accessExpiresIn: 0,
      refreshToken: '',
      refreshExpiresIn: 0,
    }
  : {
      token: '',
      expiresIn: 0,
    };

export const useTokenStore = defineStore(
  'token',
  () => {
    // 定义用户信息
    const tokenInfo = ref<IAuthLoginRes>({ ...tokenInfoState });

    // 退出登录进行中标志，供路由拦截器跳过登录检查，避免导航竞态
    const isLoggingOut = ref(false);

    // 添加一个时间戳 ref 作为响应式依赖
    const nowTime = ref(Date.now());
    /**
     * 更新响应式数据:now
     * 确保isTokenExpired/isRefreshTokenExpired重新计算,而不是用错误过期缓存值
     * 可useTokenStore内部适时调用;也可链式调用:tokenStore.updateNowTime().hasLogin
     * @returns 最新的tokenStore实例
     */
    const updateNowTime = () => {
      nowTime.value = Date.now();
      return useTokenStore();
    };

    // 设置用户信息
    const setTokenInfo = (val: any) => {
      updateNowTime();
      tokenInfo.value = val;

      // 计算并存储过期时间
      const now = Date.now();
      if (isSingleTokenRes(val)) {
        // 单token模式
        // const expireTime = now + val.expiresIn * 1000;
        const expireTime = val.expiresIn * 1000; // 使用后台返回过期时间
        uni.setStorageSync('accessTokenExpireTime', expireTime);
      }
      else if (isDoubleTokenRes(val)) {
        // 双token模式
        const accessExpireTime = now + val.accessExpiresIn * 1000;
        const refreshExpireTime = now + val.refreshExpiresIn * 1000;
        uni.setStorageSync('accessTokenExpireTime', accessExpireTime);
        uni.setStorageSync('refreshTokenExpireTime', refreshExpireTime);
      }
    };

    /**
     * 判断token是否过期
     */
    const isTokenExpired = computed(() => {
      if (!tokenInfo.value) {
        return true;
      }

      const now = nowTime.value;
      const expireTime = uni.getStorageSync('accessTokenExpireTime');

      if (!expireTime)
        return true;
      return now >= expireTime;
    });

    /**
     * 判断refreshToken是否过期
     */
    const isRefreshTokenExpired = computed(() => {
      if (!isDoubleTokenMode)
        return true;

      const now = nowTime.value;
      const refreshExpireTime = uni.getStorageSync('refreshTokenExpireTime');

      if (!refreshExpireTime)
        return true;
      return now >= refreshExpireTime;
    });

    /**
     * 登录成功后处理逻辑
     * @param tokenInfo 登录返回的token信息
     */
    async function _postLogin(tokenInfo: any) {
      setTokenInfo(tokenInfo);
      // 保存后获取用户信息
      await useUserStore().fetchUserInfo();
    }

    /**
     * 用户登录
     * 有的时候后端会用一个接口返回token和用户信息，有的时候会分开2个接口，一个获取token，一个获取用户信息
     * （各有利弊，看业务场景和系统复杂度），这里使用2个接口返回的来模拟
     * @returns 登录结果
     */
    const login = async (loginResult: API.LoginResult) => {
      try {
        const result = {
          token: loginResult.token,
          expiresIn: loginResult.out_time,
        };

        console.log('普通登录-res: ', result);
        await _postLogin(result);
        return loginResult;
      }
      catch (error) {
        console.error('登录失败:', error);
        throw error;
      }
      finally {
        updateNowTime();
      }
    };

    /**
     * 微信登录
     * 有的时候后端会用一个接口返回token和用户信息，有的时候会分开2个接口，一个获取token，一个获取用户信息
     * （各有利弊，看业务场景和系统复杂度），这里使用2个接口返回的来模拟
     * @returns 登录结果
     */
    const wxLogin = async () => {
      try {
        // 获取微信小程序登录的code
        const code = await getWxCode();
        console.log('微信登录-code: ', code);
        const res = await _wxLogin(code);
        console.log('微信登录-res: ', res);
        await _postLogin(res);
        uni.showToast({
          title: '登录成功',
          icon: 'success',
        });
        return res;
      }
      catch (error) {
        console.error('微信登录失败:', error);
        uni.showToast({
          title: '微信登录失败，请重试',
          icon: 'error',
        });
        throw error;
      }
      finally {
        updateNowTime();
      }
    };

    /**
     * 退出登录 并 删除用户信息
     */
    const logout = async () => {
      try {
        // TODO 实现自己的退出登录逻辑
        await _logout();
      }
      catch (error) {
        console.error('退出登录失败:', error);
      }
      finally {
        updateNowTime();

        // 无论成功失败，都需要清除本地token信息
        // 清除存储的过期时间
        uni.removeStorageSync('accessTokenExpireTime');
        uni.removeStorageSync('refreshTokenExpireTime');
        tokenInfo.value = { ...tokenInfoState };
        uni.removeStorageSync('token');
        const userStore = useUserStore();
        userStore.clearUserInfo();
      }
    };

    /**
     * 刷新token
     * @returns 刷新结果
     */
    const refreshToken = async () => {
      if (!isDoubleTokenMode) {
        console.error('单token模式不支持刷新token');
        throw new Error('单token模式不支持刷新token');
      }

      try {
        // 安全检查，确保refreshToken存在
        if (!isDoubleTokenRes(tokenInfo.value) || !tokenInfo.value.refreshToken) {
          throw new Error('无效的refreshToken');
        }

        const refreshToken = tokenInfo.value.refreshToken;
        const res = await _refreshToken(refreshToken);
        console.log('刷新token-res: ', res);
        setTokenInfo(res);
        return res;
      }
      catch (error) {
        console.error('刷新token失败:', error);
        throw error;
      }
      finally {
        updateNowTime();
      }
    };

    /**
     * 获取有效的token
     * 注意：在computed中不直接调用异步函数，只做状态判断
     * 实际的刷新操作应由调用方处理
     * 建议这样使用 tokenStore.updateNowTime().validToken
     */
    const getValidToken = computed(() => {
      // token已过期，返回空
      if (isTokenExpired.value) {
        return '';
      }

      if (!isDoubleTokenMode) {
        return isSingleTokenRes(tokenInfo.value) ? tokenInfo.value.token : '';
      }
      else {
        return isDoubleTokenRes(tokenInfo.value) ? tokenInfo.value.accessToken : '';
      }
    });

    /**
     * 检查是否有登录信息（不考虑token是否过期）
     */
    const hasLoginInfo = computed(() => {
      if (!tokenInfo.value) {
        return false;
      }
      if (isDoubleTokenMode) {
        return isDoubleTokenRes(tokenInfo.value) && !!tokenInfo.value.accessToken;
      }
      else {
        return isSingleTokenRes(tokenInfo.value) && !!tokenInfo.value.token;
      }
    });

    /**
     * 检查是否已登录且token有效
     * 建议这样使用tokenStore.updateNowTime().hasLogin
     */
    const hasValidLogin = computed(() => {
      return hasLoginInfo.value && !isTokenExpired.value;
    });

    /**
     * 尝试获取有效的token，如果过期且可刷新，则刷新token
     * @returns 有效的token或空字符串
     */
    const tryGetValidToken = async (): Promise<string> => {
      updateNowTime();
      if (!getValidToken.value && isDoubleTokenMode && !isRefreshTokenExpired.value) {
        try {
          await refreshToken();
          return getValidToken.value;
        }
        catch (error) {
          console.error('尝试刷新token失败:', error);
          return '';
        }
      }
      return getValidToken.value;
    };

    /**
     * 统一处理登录失效（token过期/被踢出）
     *
     * 导航策略：
     * 1. 当前页是 tabbar 主页 → 仅清除登录状态，不跳转
     * 2. 当前页设置了 needLogin → 清除状态后 switchTab 到最近的 tabbar 主页
     * 3. 当前页未设置 needLogin → 仅清除登录状态，不跳转
     *
     * @param message 服务端返回的提示信息，可选
     */
    const handleTokenExpired = (message?: string) => {
      // 防止重复触发（手动退出 或 多个并发请求同时失效）
      if (isLoggingOut.value) return;
      isLoggingOut.value = true;

      // 1. 清除本地 token 和用户信息（不调用退出 API，因为 token 已失效）
      _clearLocalAuth();

      // 2. 提示用户
      msg(message || '登录已失效，请重新登录', 'warning');

      // 工作台模式（白名单策略）：全包强制登录，失效后统一 reLaunch 回登录页（页面栈只保留登录页）
      if (isNeedLoginMode) {
        uni.reLaunch({
          url: LOGIN_PAGE,
          complete: () => _resetLoggingOutFlag(),
        });
        return;
      }

      // 3. 智能导航
      const pages = getCurrentPages();
      const currentPage = pages[pages.length - 1] as any;
      const currentPath = currentPage ? `/${currentPage.route}` : '';

      if (!currentPath) {
        _resetLoggingOutFlag();
        return;
      }

      // 场景A：当前页是 tabbar 主页 → 不跳转，页面自行刷新登录态
      if (isPageTabbar(currentPath)) {
        _resetLoggingOutFlag();
        return;
      }

      // 查询当前页面是否配置了 needLogin: true
      const needLoginPages = getAllPages('needLogin');
      const currentPageNeedLogin = needLoginPages.some(p => p.path === currentPath);

      if (currentPageNeedLogin) {
        // 场景B：当前页需要登录 → 找到页面栈中最近的 tabbar 页面作为返回目标
        const targetTabbar = _findNearestTabbar(pages);
        uni.switchTab({
          url: targetTabbar,
          complete: () => _resetLoggingOutFlag(),
        });
      }
      else {
        // 场景C：当前页不需要登录 → 不跳转，让页面自行处理未登录态
        _resetLoggingOutFlag();
      }
    };

    /**
     * 清除本地认证信息（不调用退出API）
     */
    const _clearLocalAuth = () => {
      updateNowTime();
      uni.removeStorageSync('accessTokenExpireTime');
      uni.removeStorageSync('refreshTokenExpireTime');
      tokenInfo.value = { ...tokenInfoState };
      uni.removeStorageSync('token');
      useUserStore().clearUserInfo();
    };

    /**
     * 从页面栈中查找最近的 tabbar 页面，找不到则返回首页
     */
    const _findNearestTabbar = (pages: any[]): string => {
      for (let i = pages.length - 1; i >= 0; i--) {
        const pagePath = `/${pages[i].route}`;
        if (isPageTabbar(pagePath)) {
          return pagePath;
        }
      }
      return HOME_PAGE;
    };

    /**
     * 延迟重置 isLoggingOut 标志
     */
    const _resetLoggingOutFlag = () => {
      setTimeout(() => {
        isLoggingOut.value = false;
      }, 500);
    };

    return {
      // 核心API方法
      login,
      wxLogin,
      logout,
      handleTokenExpired,

      // 退出登录标志（路由拦截器使用）
      isLoggingOut,

      // 认证状态判断（最常用的）
      hasLogin: hasValidLogin,

      // 内部系统使用的方法
      refreshToken,
      tryGetValidToken,
      validToken: getValidToken,

      // 调试或特殊场景可能需要直接访问的信息
      tokenInfo,
      setTokenInfo,
      updateNowTime,
    };
  },
  {
    // 添加持久化配置，确保刷新页面后token信息不丢失
    // 注意：只持久化 tokenInfo，排除 nowTime（驱动 computed 的技术 ref）和 isLoggingOut（瞬态标志）
    persist: {
      paths: ['tokenInfo'],
    },
  },
);
